// canvas/scoreboard.js: top-right score panel (M2.c-5 → M2.c-6).
//
// Two-row layout inside layout.scoreboard:
//   row 1 (upper half): trump pip + "US N | THEM M"
//   row 2 (lower half): "Hand K / N" muted
//
// Row 1 is the M2.c-5 surface and stays unchanged visually. Row 2 is
// the M2.c-6 addition: a running hand index against the configured
// game target so the player sees how close either team is to winning.
//
// Data contract:
//   state.scores       = { team_a: number, team_b: number }
//   state.trump_suit   = 'S'|'H'|'D'|'C'|null
//   state.hand_index   = number   (1-based; falls back to 0 if missing)
//   state.game_target  = number   (default 10 if missing)
//
// Pre-bid (trump_suit=null): the pip simply does not render; the rest
// of the panel stays, scores at 0-0 and "Hand 1 / 10" are still
// useful glance-down info.

import { renderTrumpPip } from './trump-badge.js';
import { canvasFont, fitFontSize } from './text.js';

const COLOR_PANEL_BG    = '#2A2A2A';
const COLOR_PANEL_BORDER= '#3A3A3A';
const COLOR_LABEL       = '#9A9A9A';
const COLOR_TEAM_GOLD   = '#F0C040';  // US (team A, player + partner)
const COLOR_TEAM_TEAL   = '#3DC4C7';  // THEM (team B, opponents)

const DEFAULT_GAME_TARGET = 10;

function fillRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.fill();
}

function strokeRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.stroke();
}

/**
 * Format a single team's "LABEL N" output. Pure, exported so unit tests
 * can verify formatting without a canvas context.
 *
 * @param {string} label
 * @param {number|undefined|null} score
 * @returns {string}
 */
export function formatTeamScore(label, score) {
  const n = (typeof score === 'number' && Number.isFinite(score)) ? score : 0;
  return `${label} ${n}`;
}

/**
 * Format the hand-progress line. Falls back to "Hand 1 / 10" when fields
 * are missing so the row never renders garbage. Hand index is bumped to
 * at least 1, the player thinks of "the first hand" as hand 1, not 0,
 * even though state.lua tracks it 0-based until the deal starts.
 *
 * @param {number|undefined|null} hand_index
 * @param {number|undefined|null} game_target
 * @returns {string}
 */
export function formatHandProgress(hand_index, game_target) {
  const target = (typeof game_target === 'number' && Number.isFinite(game_target) && game_target > 0)
    ? game_target
    : DEFAULT_GAME_TARGET;
  // T20: the engine's hand_index is 0-based, display is idx + 1. The
  // old Math.max(1, idx) clamp treated it as 1-based, which masked the
  // hand-2 wedge for weeks: hand 2 (idx 1) still displayed "Hand 1".
  // And "Hand 1 / 10" read as "1 of 10 hands" when 10 is the POINT
  // target, say what it means.
  const idx = (typeof hand_index === 'number' && Number.isFinite(hand_index) && hand_index >= 0)
    ? Math.floor(hand_index) + 1
    : 1;
  return `Hand ${idx} · First to ${target}`;
}

/**
 * Render the scoreboard panel inside layout.scoreboard.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   trump_suit?:   string|null,
 *   scores?:       { team_a?: number, team_b?: number } | null,
 *   hand_index?:   number|null,
 *   game_target?:  number|null,
 * }} state
 * @param {import('./layout.js').TableLayout} layout
 */
export function renderScoreboard(ctx, state, layout, opts = {}) {
  // Review blocker fix: US = the VIEWER's team. Hot-seat and the hero
  // omit opts and keep the team_a default.
  const myTeam = opts.myTeam === 'team_b' ? 'team_b' : 'team_a';
  const otherTeam = myTeam === 'team_a' ? 'team_b' : 'team_a';
  const rect = layout.scoreboard;
  if (!rect) return;
  const { x, y, w, h } = rect;
  const isMobile = !!layout.isMobile;

  // Panel background.
  ctx.fillStyle = COLOR_PANEL_BG;
  fillRoundRect(ctx, x, y, w, h, 6);
  ctx.strokeStyle = COLOR_PANEL_BORDER;
  ctx.lineWidth = 1;
  strokeRoundRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 6);

  // Two-row split. Row 1 takes the top ~60% (scores), row 2 the bottom
  // ~40% (hand progress), scores get more height because they're the
  // primary glance-down target.
  const row1H = Math.round(h * 0.6);
  const row1CY = y + Math.round(row1H / 2);
  const row2CY = y + row1H + Math.round((h - row1H) / 2);

  // -------- Row 1: trump pip + US + sep + THEM --------
  const pipMax = isMobile ? 18 : 22;
  const pipSize = Math.min(pipMax, row1H - 6);
  const pipCX = x + 4 + Math.round(pipSize / 2);
  if (state.trump_suit) {
    renderTrumpPip(ctx, state.trump_suit, pipCX, row1CY, pipSize);
  }

  const textLeft = x + (state.trump_suit ? (4 + pipSize + 6) : 8);
  const textRight = x + w - 6;
  const textW = Math.max(40, textRight - textLeft);
  ctx.textBaseline = 'middle';
  ctx.font = canvasFont(isMobile ? 11 : 13, { weight: 600 });
  ctx.textAlign = 'left';

  const scores = state.scores || {};
  const usText   = formatTeamScore('US',   scores[myTeam]);
  const themText = formatTeamScore('THEM', scores[otherTeam]);

  const midX = textLeft + Math.round(textW / 2);
  ctx.fillStyle = COLOR_TEAM_GOLD;
  ctx.fillText(usText, textLeft, row1CY);

  // Separator pipe centered between the two scores in row 1 only, the
  // bottom row reads as one continuous string and doesn't need it.
  ctx.fillStyle = COLOR_LABEL;
  const sepTop = y + 4;
  const sepBot = y + row1H - 4;
  ctx.fillRect(midX - 1, sepTop, 1, Math.max(8, sepBot - sepTop));

  ctx.fillStyle = COLOR_TEAM_TEAL;
  ctx.fillText(themText, midX + 6, row1CY);

  // -------- Row 2: "Hand K · First to N" muted, centered --------
  // T20 made this string ~2x longer; shrink-to-fit so it never paints
  // past the panel on the 116px mobile/portrait widths (review #4).
  ctx.fillStyle = COLOR_LABEL;
  const progress = formatHandProgress(state.hand_index, state.game_target);
  const rowPad = 6;
  const fitPx = fitFontSize(progress, w - rowPad * 2, (px) => {
    ctx.font = canvasFont(px);
    return ctx.measureText(progress).width;
  }, { basePx: isMobile ? 11 : 12, minPx: 8 });
  ctx.font = canvasFont(fitPx);
  ctx.textAlign = 'center';
  ctx.fillText(progress, x + Math.round(w / 2), row2CY);
}

export const _internals = {
  COLOR_PANEL_BG,
  COLOR_PANEL_BORDER,
  COLOR_LABEL,
  COLOR_TEAM_GOLD,
  COLOR_TEAM_TEAL,
  DEFAULT_GAME_TARGET,
  formatTeamScore,
  formatHandProgress,
};
